// Workspace context — multi-role users (2026-09-04).
// Loads /api/workspace/me once per session: the user's roles, the role they are WORKING IN right now (active role),
// the union of their permissions, the app functions they are authorized for, and the shared mailboxes their roles
// may open. Authorization never depends on the chosen role — a user may do everything any of their roles allows;
// the active role only sets the default view, the bottom bar and which mailboxes are offered first.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

export interface WsRole { id: number; name: string; description?: string | null }
export interface WsFunction { key: string; label: string; path: string; desktop: string; icon: string }
export interface WsMailbox { address: string; label: string; roles: string[]; unseen?: number; has_mailbox?: boolean }
export interface Workspace {
  user: { id: number; email: string; first_name: string; last_name: string; is_superadmin: boolean };
  roles: WsRole[];
  active_role: WsRole | null;
  needs_role_choice: boolean;
  permissions: string[];
  functions: WsFunction[];
  nav: string[];
  mailboxes: WsMailbox[];
  all_mailboxes: WsMailbox[];
}

interface Ctx {
  ws: Workspace | null;
  loading: boolean;
  reload: () => Promise<void>;
  setActiveRole: (roleId: number) => Promise<void>;
  can: (perm?: string | null) => boolean;
  fn: (key: string) => WsFunction | undefined;
  activeRoleName: string;
}

const WorkspaceContext = createContext<Ctx | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const [ws, setWs] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!isAuthenticated) { setWs(null); return; }
    setLoading(true);
    try { const r = await api.get('/workspace/me'); setWs(r.data); }
    catch (_) { /* keep whatever we had; pages still work without the workspace */ }
    finally { setLoading(false); }
  }, [isAuthenticated]);

  useEffect(() => { reload(); }, [reload, user?.id]);

  const setActiveRole = useCallback(async (roleId: number) => {
    const r = await api.put('/workspace/active-role', { role_id: roleId });
    setWs(r.data);
  }, []);

  const value = useMemo<Ctx>(() => ({
    ws, loading, reload, setActiveRole,
    can: (perm?: string | null) => !perm || !!ws && (ws.permissions.includes('*') || ws.permissions.includes(perm)),
    fn: (key: string) => ws?.functions.find((f) => f.key === key),
    activeRoleName: ws?.active_role?.name || (ws?.roles?.length === 1 ? ws.roles[0].name : ''),
  }), [ws, loading, reload, setActiveRole]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): Ctx {
  const c = useContext(WorkspaceContext);
  if (!c) return { ws: null, loading: false, reload: async () => {}, setActiveRole: async () => {}, can: () => true, fn: () => undefined, activeRoleName: '' };
  return c;
}
